import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';

import {
    BodyTextSmall,
    HeadingText30,
    HeadingText60,
    Icon,
    SkeletonPlaceholder,
    pxToRem,
    CalloutInline,
} from '@modernhealth/vital-components';

import { TrackClick } from '@modernhealth/web-analytics';
import { filterNullish } from '@modernhealth/filter-nullish';
import { useHistory, Redirect, useLocation } from 'react-router-dom';
import { colors, layout } from '@modernhealth/vital-foundation';
import * as Sentry from '@sentry/browser';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import queryString from 'query-string';

import { InternalLinkButton, Button } from 'src/shared/analytics/components';
import { ContentCollectionPlatform, InternalLinkNode } from 'src/generated/webapp_gql';
import { navigationService } from 'src/config/rootService';
import { DropdownMenu, DropdownMenuItem } from 'src/shared/components/DropdownMenu';
import { Avatar } from 'src/modules/messaging/components/Avatar';
import { useManageCareTeamScreenQuery } from 'src/modules/care/documents/manageCareTeam.generated';
import { ProviderDetailsModal } from 'src/modules/providers/components/ProviderDetailsModal';
import { getMyProviders } from 'src/modules/providers/selectors/providersSelectors';
import { ChangeProviderModal } from 'src/modules/care/components/ChangeProviderModal';
import { RemoveProviderModal } from 'src/modules/care/components/RemoveProviderModal';
import { SOURCE_TYPES } from 'src/modules/user/models/user';
import { CrisisModal } from 'src/shared/components/CrisisModal';
import { ManageCareTeamTestID } from 'src/modules/care/pages/testIds';

export const ManageCareTeam = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { search } = useLocation();
    const providers = useSelector(getMyProviders);
    const { removeUuid, changeUuid, profileUuid } = queryString.parse(search);
    const [isCrisisModalOpen, setIsCrisisModalOpen] = useState(false);
    const { data, loading, refetch } = useManageCareTeamScreenQuery({
        variables: {
            platform: ContentCollectionPlatform.Web,
        },
        fetchPolicy: 'cache-and-network',
    });

    const providerUuid = removeUuid || changeUuid || profileUuid;
    const selectedProvider = useMemo(
        () => providers.find(p => p.uuid === providerUuid),
        [providerUuid, providers],
    );
    const screenSection = data?.manageCareTeamScreen?.screenSections ?? [];

    if (loading) {
        return (
            <Container data-testid={ManageCareTeamTestID.Loader}>
                <SkeletonPlaceholder width={300} height={40} marginTop={40} />
                <SkeletonPlaceholder width={500} height={20} marginTop={14} marginBottom={84} />
                <ActionsContainer>
                    {[{}, {}, {}].map((_, index) => (
                        <ProviderActionItem key={`placeholder_${index}`} isFirst={index === 0}>
                            <SkeletonPlaceholder width={60} height={60} borderRadius={30} />
                            <ItemContent>
                                <SkeletonPlaceholder width={100} height={20} />
                                <SkeletonPlaceholder width={300} height={18} marginTop={4} />
                            </ItemContent>
                        </ProviderActionItem>
                    ))}
                </ActionsContainer>
            </Container>
        );
    }

    //right now we dont have to handle more than one section
    if (screenSection.length > 1) {
        Sentry.captureMessage('ManageCareTeam Screen received more than 1 screen section');
    }

    if (screenSection.length === 0) {
        Sentry.captureMessage(
            'ManageCareTeam Screen received 0 screen sections',
            Sentry.Severity.Error,
        );
        return <Redirect to="/" />;
    }

    const actions = (screenSection[0].contentCollectionLinks as InternalLinkNode[]) ?? [];

    /**
     *  This calculates the maxWidth for the subtitle
     *  if the action has childLinks.
     */
    const subTitleMaxWidth = (action: InternalLinkNode) => {
        let maxWidth = '100%';
        if (action && action.childLinks && action.subtitle && action.subtitle.length > 40)
            maxWidth = '90%';
        return maxWidth;
    };

    return (
        <Container>
            <TrackClick eventName="back button">
                <IconContainer onClick={() => history.goBack()}>
                    <Icon name="arrow-left" />
                </IconContainer>
            </TrackClick>
            <HeadingText60 centered css={{ marginBottom: pxToRem(13) }}>
                {t('care:manage_care_team.title')}
            </HeadingText60>
            <BodyTextSmall centered marginBottom={64}>
                {t('care:manage_care_team.description')}
            </BodyTextSmall>
            <ActionsContainer>
                {actions.map((action, index) => {
                    const hasLink = action.path.includes('/');
                    const ProviderActionItemContainer = hasLink
                        ? ProviderActionItemButton
                        : ProviderActionItem;
                    const [primaryLink, ...nullableChildLinks] = Array.isArray(action.childLinks)
                        ? action.childLinks
                        : [];
                    const childLinks = filterNullish(nullableChildLinks);
                    const hasChildLinks = childLinks.length > 0;

                    return (
                        <>
                            <ProviderActionItemContainer
                                isFirst={index === 0}
                                key={action.uuid}
                                eventName={action.titleTranslationKey}
                                eventProperties={{
                                    element_index: index,
                                }}
                                {...(hasLink
                                    ? {
                                          onClick: () => history.push(action.path),
                                          variant: 'flat',
                                          fluid: true,
                                      }
                                    : {})}
                            >
                                <ProviderAvatar
                                    image={action.image ?? undefined}
                                    diameter={60}
                                    initials={action.providerInitials ?? undefined}
                                />
                                <ItemContent>
                                    <HeadingText30>{action.title}</HeadingText30>
                                    <BodyTextSmall maxWidth={subTitleMaxWidth(action)}>
                                        {action.subtitle}
                                    </BodyTextSmall>
                                </ItemContent>
                                {!!primaryLink || hasChildLinks ? (
                                    <>
                                        {!!primaryLink && (
                                            <InternalLinkButton
                                                role="link"
                                                size="medium"
                                                disabled={primaryLink.disabled || false}
                                                variant="secondary"
                                                css={{
                                                    marginLeft: 'auto',
                                                    marginRight: pxToRem(10),
                                                }}
                                                eventName={primaryLink.titleTranslationKey}
                                                to={primaryLink.path}
                                            >
                                                {primaryLink.title}
                                            </InternalLinkButton>
                                        )}
                                        {hasChildLinks && (
                                            <DropdownMenu>
                                                {childLinks.map((link, index) => (
                                                    <TrackClick
                                                        key={link.title}
                                                        eventName={link.titleTranslationKey}
                                                        eventProperties={{
                                                            element_index: index,
                                                        }}
                                                    >
                                                        <DropdownMenuItem
                                                            title={link.title}
                                                            linkTo={link.path}
                                                            color={
                                                                link.colorScheme === 'RED'
                                                                    ? 'red55'
                                                                    : 'gray65'
                                                            }
                                                        />
                                                    </TrackClick>
                                                ))}
                                            </DropdownMenu>
                                        )}
                                    </>
                                ) : (
                                    <Icon
                                        name="chevron-right"
                                        css={{ marginLeft: 'auto' }}
                                        size={20}
                                    />
                                )}
                            </ProviderActionItemContainer>
                            {primaryLink?.additionalInformationTitle &&
                                primaryLink?.additionalInformation && (
                                    <CalloutInline
                                        variant="default"
                                        css={{ marginBottom: pxToRem(20) }}
                                        heading={primaryLink?.additionalInformationTitle}
                                    >
                                        {primaryLink?.additionalInformation}
                                    </CalloutInline>
                                )}
                        </>
                    );
                })}
            </ActionsContainer>
            <CrisisElementsContainer>
                <BodyTextSmall>{t('shared:crisis_support_banner.message')}</BodyTextSmall>
                <CrisisLinkButton
                    eventName="benefits:care_navigation_topic_step.view_crisis_resources"
                    onClick={() => {
                        setIsCrisisModalOpen(true);
                    }}
                    variant="flat"
                >
                    <BodyTextSmall underline fontColor="blue55" marginLeft={3}>
                        {t('benefits:care_navigation_topic_step.view_crisis_resources')}
                    </BodyTextSmall>
                </CrisisLinkButton>
            </CrisisElementsContainer>

            <CrisisModal
                visible={isCrisisModalOpen}
                onClose={() => {
                    setIsCrisisModalOpen(false);
                }}
                source={SOURCE_TYPES.CALLBACK_SCREEN}
            />
            {!!selectedProvider && (
                <>
                    <ProviderDetailsModal
                        isVisible={!!profileUuid}
                        providerUuid={selectedProvider.uuid}
                        onClose={() => navigationService.replace('/care-team')}
                    />
                    <RemoveProviderModal
                        isVisible={!!removeUuid}
                        providerUuid={selectedProvider.uuid}
                        onConfirm={async () => {
                            await refetch();
                        }}
                        onCancel={() => navigationService.replace('/care-team')}
                    />
                    <ChangeProviderModal
                        isVisible={!!changeUuid}
                        providerUuid={selectedProvider.uuid}
                        onCancel={() => {
                            navigationService.replace('/care-team');
                        }}
                    />
                </>
            )}
        </Container>
    );
};

const Container = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
    marginTop: pxToRem(32),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const ActionsContainer = styled.div({
    width: '100%',
    maxWidth: pxToRem(550),
});

const ProviderActionItem = styled.div<{ isFirst: boolean }>(({ isFirst }) => ({
    margin: 0,
    paddingTop: pxToRem(39),
    paddingBottom: pxToRem(39),
    color: colors.gray65,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',

    borderTop: isFirst ? undefined : `${pxToRem(2)} solid ${colors.gray05}`,
}));

const ProviderActionItemButton = styled(ProviderActionItem.withComponent(Button))({
    paddingLeft: 0,
    justifyContent: 'flex-start',
    backgroundColor: 'transparent',
});

const IconContainer = styled.div({
    height: pxToRem(40),
    alignSelf: 'flex-start',
    cursor: 'pointer',
});

const ItemContent = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginLeft: pxToRem(16),
});

const ProviderAvatar = styled(Avatar)({
    borderColor: colors.violet40,
    backgroundColor: colors.violet40,
});

const CrisisElementsContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: pxToRem(40),
});

const CrisisLinkButton = styled(Button)({
    padding: 0,
});
